import type { TestContentWithSimpleSteps } from '~/server/api/routers/test';
import { Text } from '@mantine/core';
import React, { useMemo } from 'react';

const MAX_NON_FILTERED_STEPS_TO_SHOW = 3;

export function MatchingStepsWithEllipses({
  simpleSteps,
  searchTerm,
}: {
  simpleSteps: TestContentWithSimpleSteps['steps'];
  searchTerm: string;
}) {
  // No steps to show
  if (!simpleSteps.length) {
    return null;
  }

  // No search term - show first few idle steps with ellipses if more exist
  if (!searchTerm) {
    const firstSteps = simpleSteps.slice(0, MAX_NON_FILTERED_STEPS_TO_SHOW);
    const hasMoreSteps = simpleSteps.length > MAX_NON_FILTERED_STEPS_TO_SHOW;

    return (
      <>
        {firstSteps.map((step, i) => (
          <Step
            key={step.id}
            stepDescription={step.description}
            index={i}
          />
        ))}
        {!!hasMoreSteps && <Ellipsis />}
      </>
    );
  }

  // Has search term - show filtered results with ellipses
  return (
    <StepsWithEllipses
      simpleSteps={simpleSteps}
      searchTerm={searchTerm}
    />
  );
}

function StepsWithEllipses({
  simpleSteps,
  searchTerm,
}: {
  simpleSteps: TestContentWithSimpleSteps['steps'];
  searchTerm: string;
}) {
  // Filter by search term
  const matchedSteps = useMemo(() => {
    if (!searchTerm) {
      return [];
    }
    return simpleSteps.filter((step) => step.description.toLowerCase().includes(searchTerm.toLowerCase()));
  }, [simpleSteps, searchTerm]);

  // We want to know the original indices of the matches so we can show the correct step numbers
  const matchIndices = useMemo(() => {
    return matchedSteps.map((match) => simpleSteps.findIndex((step) => step.id === match.id));
  }, [matchedSteps, simpleSteps]);

  return (
    <>
      {matchedSteps.map((step, i) => {
        // Matched step indices
        const stepIndex = matchIndices.at(i) ?? 0;
        const nextStepIndex = matchIndices.at(i + 1) ?? 0;

        // If isFirst or isLast is true then there are still steps before and after; show ellipsis
        const isFirst = i === 0 && stepIndex > 0;
        const isLast = i === matchedSteps.length - 1 && stepIndex < simpleSteps.length - 1;
        const hasGapAfter = nextStepIndex - stepIndex > 1;

        return (
          <Step
            key={step.id}
            stepDescription={step.description}
            index={stepIndex}
            isFirst={isFirst}
            isLast={isLast}
            hasGapAfter={hasGapAfter}
          />
        );
      })}
    </>
  );
}

function Step({
  stepDescription,
  index,
  isFirst = false,
  isLast = false,
  hasGapAfter = false,
}: {
  stepDescription: string;
  index: number;
  isFirst?: boolean;
  isLast?: boolean;
  hasGapAfter?: boolean;
}) {
  return (
    <>
      {!!isFirst && <Ellipsis />}

      <div className="flex gap-2 break-all py-1 text-gray-600 dark:text-gray-400">
        <Text
          size="xs"
          c="dimmed"
        >
          {index + 1}.
        </Text>
        <Text size="xs">{stepDescription}</Text>
      </div>

      {!!hasGapAfter && <Ellipsis />}

      {!!isLast && <Ellipsis />}
    </>
  );
}

function Ellipsis() {
  return <div className="py-1 text-xs text-gray-600 dark:text-gray-400">…</div>;
}
